import '@fontsource/titillium-web';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
    Box,
    Container,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import React from 'react';

type SenseiId = 'yonesato' | 'kuramoto' | 'patrick';

type GroupKind =
    | 'enfants'
    | 'tous-adultes'
    | 'shodan-nidan'
    | 'sandan-up'
    | 'kyu'
    | 'dan'
    | '10-5kyu'
    | '4-1kyu'
    | 'plain';

type ScheduleEntry = {
    groupLabel: string;
    groupKind: GroupKind;
    sensei: SenseiId;
};

type ScheduleBlock =
    | { type: 'rows'; time: string; entries: ScheduleEntry[] }
    | {
          type: 'banner';
          time: string;
          label: string;
          banner: 'examen-dan' | 'tous-adultes';
      };

const SENSEI_BG: Record<SenseiId, string> = {
    yonesato: '#b8d4f0',
    kuramoto: '#c5e6c5',
    patrick: '#ffd4b8',
};

const SENSEI_LABEL: Record<SenseiId, string> = {
    yonesato: 'Sensei Yonesato',
    kuramoto: 'Sensei Kuramoto',
    patrick: 'Sensei Patrick',
};

const groupTextColor = (kind: GroupKind): string => {
    const map: Record<GroupKind, string> = {
        enfants: '#c62828',
        'tous-adultes': '#2e7d32',
        'shodan-nidan': '#1565c0',
        'sandan-up': '#1a1a1a',
        kyu: '#6a1b9a',
        dan: '#e65100',
        '10-5kyu': '#ef6c00',
        '4-1kyu': '#1a1a1a',
        plain: '#1a1a1a',
    };
    return map[kind];
};

const styles = {
    container: {
        width: '100%',
        overflowX: 'auto',
        pt: 2,
        pb: 2,
        minHeight: 'calc(100vh - 56px)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
    },
    title: {
        textAlign: 'center',
        textDecoration: 'underline',
        mb: 2,
        fontWeight: 600,
    },
    tablesStack: {
        width: '100%',
        maxWidth: 520,
        mx: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
    },
    tableContainer: {
        mb: 0,
        width: '100%',
    },
    dayHeaderCell: {
        fontWeight: 'bold',
        fontSize: { xs: '0.85rem', sm: '1.05rem' },
        textAlign: 'center',
        backgroundColor: '#546e7a',
        color: '#fff',
        borderBottom: '1px solid rgba(0,0,0,0.2)',
    },
    headerCell: {
        fontWeight: 'bold',
        fontSize: '0.75rem',
        backgroundColor: '#f0f0f0',
        color: '#1a1a1a',
    },
    timeCell: {
        fontSize: '0.8rem',
        fontWeight: 600,
        verticalAlign: 'top',
        backgroundColor: '#e8e8e8',
        color: '#1a1a1a',
        minWidth: '108px',
    },
    groupCell: {
        fontSize: '0.8rem',
        verticalAlign: 'top',
        backgroundColor: '#fff',
    },
    senseiCell: {
        fontSize: '0.8rem',
        verticalAlign: 'top',
        fontWeight: 500,
    },
    iconButton: {
        backgroundColor: 'primary.main',
        color: 'white',
        marginTop: '24px',
        marginBottom: '5vh',
        '&:hover': {
            backgroundColor: 'primary.dark',
        },
        width: '48px',
        height: '48px',
    },
};

const friday: { day: string; blocks: ScheduleBlock[] } = {
    day: 'Friday',
    blocks: [
        {
            type: 'rows',
            time: '18.00 - 19.00',
            entries: [
                {
                    groupLabel: 'Children',
                    groupKind: 'enfants',
                    sensei: 'yonesato',
                },
                {
                    groupLabel: 'All adults',
                    groupKind: 'tous-adultes',
                    sensei: 'kuramoto',
                },
            ],
        },
        {
            type: 'rows',
            time: '19.15 - 20.30',
            entries: [
                {
                    groupLabel: 'Shodan - Nidan',
                    groupKind: 'shodan-nidan',
                    sensei: 'yonesato',
                },
                {
                    groupLabel: 'Sandan - Up',
                    groupKind: 'sandan-up',
                    sensei: 'kuramoto',
                },
                {
                    groupLabel: 'Kyu',
                    groupKind: 'kyu',
                    sensei: 'patrick',
                },
            ],
        },
    ],
};

const saturday: { day: string; blocks: ScheduleBlock[] } = {
    day: 'Saturday',
    blocks: [
        {
            type: 'rows',
            time: '10.00 - 10.45',
            entries: [
                {
                    groupLabel: 'Children',
                    groupKind: 'enfants',
                    sensei: 'patrick',
                },
                {
                    groupLabel: 'Kyu',
                    groupKind: 'kyu',
                    sensei: 'yonesato',
                },
                {
                    groupLabel: 'Dan',
                    groupKind: 'dan',
                    sensei: 'kuramoto',
                },
            ],
        },
        {
            type: 'rows',
            time: '11.00 - 11.45',
            entries: [
                {
                    groupLabel: '10 kyu – 5 kyu',
                    groupKind: '10-5kyu',
                    sensei: 'kuramoto',
                },
                {
                    groupLabel: '4 kyu – 1 kyu',
                    groupKind: '4-1kyu',
                    sensei: 'patrick',
                },
                {
                    groupLabel: 'Dan',
                    groupKind: 'dan',
                    sensei: 'yonesato',
                },
            ],
        },
        {
            type: 'rows',
            time: '12.00 - 12.45',
            entries: [
                {
                    groupLabel: '10 kyu – 5 kyu',
                    groupKind: '10-5kyu',
                    sensei: 'yonesato',
                },
                {
                    groupLabel: '4 kyu – 1 kyu',
                    groupKind: '4-1kyu',
                    sensei: 'kuramoto',
                },
                {
                    groupLabel: 'Dan',
                    groupKind: 'dan',
                    sensei: 'patrick',
                },
            ],
        },
        {
            type: 'banner',
            time: '13.30 - ...',
            label: 'Dan exam',
            banner: 'examen-dan',
        },
    ],
};

const sunday: { day: string; blocks: ScheduleBlock[] } = {
    day: 'Sunday',
    blocks: [
        {
            type: 'rows',
            time: '10.00 - 10.45',
            entries: [
                {
                    groupLabel: 'Kyu',
                    groupKind: 'kyu',
                    sensei: 'patrick',
                },
                {
                    groupLabel: 'Shodan - Nidan',
                    groupKind: 'shodan-nidan',
                    sensei: 'yonesato',
                },
                {
                    groupLabel: 'Sandan - Up',
                    groupKind: 'sandan-up',
                    sensei: 'kuramoto',
                },
            ],
        },
        {
            type: 'rows',
            time: '11.00 - 11.45',
            entries: [
                {
                    groupLabel: '10 kyu – 4 kyu',
                    groupKind: '10-5kyu',
                    sensei: 'kuramoto',
                },
                {
                    groupLabel: '3 kyu - Nidan ',
                    groupKind: '4-1kyu',
                    sensei: 'patrick',
                },
                {
                    groupLabel: 'Sandan - Up',
                    groupKind: 'sandan-up',
                    sensei: 'yonesato',
                },
            ],
        },
        {
            type: 'rows',
            time: '12.00 - 12.45',
            entries: [
                {
                    groupLabel: '10 kyu – 4 kyu',
                    groupKind: '10-5kyu',
                    sensei: 'yonesato',
                },
                {
                    groupLabel: '3 kyu - Nidan ',
                    groupKind: '4-1kyu',
                    sensei: 'kuramoto',
                },
                {
                    groupLabel: 'Sandan - Up',
                    groupKind: 'sandan-up',
                    sensei: 'patrick',
                },
            ],
        },
        {
            type: 'banner',
            time: '13.00 - 13.30',
            label: 'All adults',
            banner: 'tous-adultes',
        },
    ],
};

function renderDayTable(daySchedule: { day: string; blocks: ScheduleBlock[] }) {
    const rows: React.ReactNode[] = [];

    daySchedule.blocks.forEach(block => {
        if (block.type === 'banner') {
            const sx =
                block.banner === 'examen-dan'
                    ? {
                          backgroundColor: '#ff5252',
                          color: '#1a1a1a',
                          fontWeight: 700,
                          textAlign: 'center' as const,
                      }
                    : {
                          backgroundColor: '#fff',
                          color: '#2e7d32',
                          fontWeight: 600,
                          textAlign: 'center' as const,
                      };
            rows.push(
                <TableRow
                    key={`${daySchedule.day}-${block.time}-${block.label}`}
                >
                    <TableCell sx={styles.timeCell}>{block.time}</TableCell>
                    <TableCell colSpan={2} sx={{ ...styles.groupCell, ...sx }}>
                        {block.label}
                    </TableCell>
                </TableRow>,
            );
            return;
        }

        const { time, entries } = block;
        entries.forEach((entry, i) => {
            const isFirst = i === 0;
            rows.push(
                <TableRow
                    key={`${daySchedule.day}-${time}-${entry.groupLabel}-${entry.sensei}`}
                >
                    {isFirst ? (
                        <TableCell
                            rowSpan={entries.length}
                            sx={styles.timeCell}
                        >
                            {time}
                        </TableCell>
                    ) : null}
                    <TableCell
                        sx={{
                            ...styles.groupCell,
                            color: groupTextColor(entry.groupKind),
                            fontWeight:
                                entry.groupKind === 'sandan-up' ||
                                entry.groupKind === '4-1kyu'
                                    ? 600
                                    : 500,
                        }}
                    >
                        {entry.groupLabel}
                    </TableCell>
                    <TableCell
                        sx={{
                            ...styles.senseiCell,
                            backgroundColor: SENSEI_BG[entry.sensei],
                        }}
                    >
                        {SENSEI_LABEL[entry.sensei]}
                    </TableCell>
                </TableRow>,
            );
        });
    });

    return (
        <TableContainer
            key={daySchedule.day}
            component={Paper}
            sx={styles.tableContainer}
            elevation={3}
        >
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell colSpan={3} sx={styles.dayHeaderCell}>
                            {daySchedule.day}
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell
                            sx={{ ...styles.headerCell, minWidth: '108px' }}
                        >
                            Time
                        </TableCell>
                        <TableCell sx={styles.headerCell}>
                            Class / level
                        </TableCell>
                        <TableCell
                            sx={{ ...styles.headerCell, minWidth: '140px' }}
                        >
                            Sensei
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>{rows}</TableBody>
            </Table>
        </TableContainer>
    );
}

const Schedule = ({
    scrollToSection,
}: {
    // eslint-disable-next-line no-unused-vars
    scrollToSection: (section: string) => void;
}) => (
    <Box id="schedule">
        <Container maxWidth="lg" sx={styles.container}>
            <Typography variant="h5" sx={styles.title}>
                Schedule
            </Typography>
            <Box sx={styles.tablesStack}>
                {renderDayTable(friday)}
                {renderDayTable(saturday)}
                {renderDayTable(sunday)}
            </Box>
            <IconButton
                color="primary"
                onClick={() => scrollToSection('register')}
                sx={styles.iconButton}
            >
                <KeyboardArrowDownIcon />
            </IconButton>
        </Container>
    </Box>
);

export default Schedule;
